/**
 * LOS FILTROS QUE LE FALTABAN A PAGOS DEL MES.
 *
 * Lissette, 2026-09-01 (punto 9 de 9): quiere filtros en TODAS las columnas de
 * Pagos del Mes.
 *
 * Ya se podía filtrar por tipo, aseguradora, estado USA, estado de la póliza,
 * atendido por, analista, estado del pago, monto y búsqueda libre. Las que se
 * VEN en la tabla y no se podían filtrar eran cuatro:
 *
 *     Forma      cómo se cobra: autopay, el cliente paga, forzar
 *     Método     con qué: tarjeta de crédito, débito, cuenta bancaria
 *     Nota       si la fila tiene notas o no
 *     Columnas   las que la agencia se crea a mano
 *     propias
 *
 * Es justo la queja: una columna que se ve pero por la que no se puede
 * preguntar. "¿Quiénes están en autopay y no han pagado?" no tenía respuesta
 * sin leer la tabla entera a ojo.
 *
 * ── UNA DECISIÓN QUE SE NOTA AL USARLO ─────────────────────────────────────
 *
 * "Sin definir" es una OPCIÓN, no un vacío. La mayoría de las pólizas viejas no
 * tienen forma ni método puestos, y encontrarlas para completarlas es
 * exactamente lo que hace falta para poder cobrar. Si "sin definir" no fuera
 * elegible, esas filas serían invisibles al filtro — que es como se quedan sin
 * arreglar.
 *
 * Vive fuera de `PoliciesView.jsx` para poder ejecutarlo en las pruebas sin
 * montar React ni arrastrar MSAL.
 */

/** El valor que se guarda para "no tiene ninguno". */
export const SIN_DEFINIR = 'none';

/** Las formas de pago del catálogo, en el orden en que se leen. */
export const FORMAS_DE_PAGO = ['autopay', 'client', 'force', 'zero'];

/**
 * PRIMA $0 (ticket 2026-10-12): una forma de pago más, con su etiqueta. Sigue
 * en Pagos del Mes como cualquier otra (Lissette: "que sigan apareciendo en
 * los pagos del mes igualmente, pero con la opción de pago").
 */
export const PRIMA_CERO = 'zero';

/** Los métodos de pago del catálogo. */
export const METODOS_DE_PAGO = ['credit_card', 'debit_card', 'bank_account'];

const norm = (v) => String(v ?? '').trim();

/**
 * ¿Pasa esta fila el filtro de Forma de pago?
 *
 * `'all'` no filtra. `SIN_DEFINIR` deja pasar SOLO las que no tienen forma —
 * y también las que traen un valor que no está en el catálogo, porque para la
 * agente esas filas se ven igual de vacías: la tabla no les pinta chip.
 */
export function pasaFiltroDeForma(fila, filtro) {
  if (!filtro || filtro === 'all') return true;
  const v = norm(fila?.paymentType);
  const conocida = FORMAS_DE_PAGO.includes(v);
  if (filtro === SIN_DEFINIR) return !conocida;
  return v === filtro;
}

/** Igual, para el Método de pago. */
export function pasaFiltroDeMetodo(fila, filtro) {
  if (!filtro || filtro === 'all') return true;
  const v = norm(fila?.paymentMethod);
  const conocido = METODOS_DE_PAGO.includes(v);
  if (filtro === SIN_DEFINIR) return !conocido;
  return v === filtro;
}

/**
 * ¿Tiene notas? `'con'` = al menos una, `'sin'` = ninguna.
 *
 * El contador llega de la API y no siempre es un número (puede venir como
 * texto, o no venir). Se normaliza con `Number` para DECIRLO, no porque un
 * `> 0` a secas fallara: JavaScript convierte igual, y las dos formas dan el
 * mismo resultado con cualquier entrada real. Lo que sí importa —y lo que la
 * prueba fija— es que "con" y "sin" REPARTAN todas las filas: si una no
 * cayera en ninguno de los dos, desaparecería de la tabla al filtrar.
 */
export function pasaFiltroDeNota(fila, filtro) {
  if (!filtro || filtro === 'all') return true;
  const n = Number(fila?.payment?.notesCount);
  const tiene = Number.isFinite(n) && n > 0;
  return filtro === 'con' ? tiene : !tiene;
}

/**
 * Las columnas que la agencia se crea a mano. `texto` busca DENTRO del valor,
 * sin distinguir mayúsculas ni acentos de más: son notas escritas a mano, y
 * pedir la coincidencia exacta no serviría de nada.
 *
 * Con `SIN_DEFINIR` como texto se buscan las que están VACÍAS — que es como se
 * encuentra lo que falta por rellenar.
 */
export function pasaFiltroDeColumna(fila, columnaId, texto) {
  if (!columnaId) return true;
  const buscado = norm(texto).toLowerCase();
  const valor = norm(fila?.customData?.[columnaId]);
  if (buscado === SIN_DEFINIR) return valor === '';
  if (!buscado) return true;
  return valor.toLowerCase().includes(buscado);
}

/** Cómo se lee una forma de pago. Mismos textos que el chip de la tabla. */
export function nombreDeForma(v, lang = 'es') {
  const es = lang === 'es';
  return {
    autopay: es ? '🔄 Autopay' : '🔄 Autopay',
    client: es ? '👤 Cliente paga' : '👤 Client pays',
    force: es ? '⚡ Forzar' : '⚡ Force',
    zero: es ? '🆓 Prima $0' : '🆓 $0 Premium',
    [SIN_DEFINIR]: es ? '— Sin definir' : '— Not set',
  }[v] || v;
}

/** Cómo se lee un método de pago. */
export function nombreDeMetodo(v, lang = 'es') {
  const es = lang === 'es';
  return {
    credit_card: es ? '💳 Tarjeta de Crédito' : '💳 Credit Card',
    debit_card: es ? '💳 Tarjeta de Débito' : '💳 Debit Card',
    bank_account: es ? '🏦 Cuenta Bancaria (ACH)' : '🏦 Bank Account (ACH)',
    [SIN_DEFINIR]: es ? '— Sin definir' : '— Not set',
  }[v] || v;
}
